import { defineComponent as _defineComponent } from 'vue'
import { defineMessages } from '@/lib/i18n';
import { FormItem } from '@/interfaces/lib/model';
import { getJoinedDepartmentName } from '@/utils/string';
import { buildDepartmentPathMap } from '@/utils/departmentTree';
import { usePermissionComposable } from '@/composables/usePermission';
import { SortState } from '@/composables/useSds';
import {
  PropType,
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
} from 'vue';

import { Sds, SdsProduct } from '@/interfaces/model/general/sds';
import { Department } from '@/interfaces/model/general/department';
import { StatusId, RevisionStatus, CountryCode } from '@/lib/enum';
import { omitString } from '@/lib/formatter';
import { formatToDate } from '@/utils/datetime';
import { casNumberPattern } from '@/lib/regex';
import SdsTablePseudoScrollBar from '@/views/dashboard/SdsTablePseudoScrollBar.vue';
import { TagResponse } from '@/interfaces/model/general/tag';
import TagChip from '@/components/TagChip.vue';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import { useDepartmentComposable } from '@/composables/useDepartment';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import { RISK_ASSESSMENT_USER_GUIDE_URL, riskAssessmentBaseUrl } from '@/env';
import { getRiskLevelChipColor } from '@/composables/useSdsFilter';
import { store } from '@/store/index';
import { readAssertedCompany } from '@/store/main/getters';


export default /*#__PURE__*/_defineComponent({
  __name: 'CompanySdsTable',
  props: {
  safetyDataSheets: {
    type: Array as PropType<Array<Sds>>,
    default: () => [
      {
        id: 1,
        uuid: '5bcee672-8a7b-4c69-8929-7d7653adfa59',
        name: 'ethanol.pdf',
        note: 'メモ',
        admin_note: 'メモ',
        timestamp: 1560000000,
        status_id: 10000,
        company_id: '9b0186d2-26fc-4dc8-833d-14904d31610e',
        user_id: '09c94b5b-feb4-430f-b1e5-aa18c7587e17',
        published_at: '2022-10-03',
        assessed_at: '2022-10-03',
        revised_at: '2022-10-03',
        created_at: '2022-10-03T06:40:48.580289',
        updated_at: '2022-10-03T06:40:48.580289',
        deleted_at: null,
      },
    ],
    required: true,
  },
  suppressPaginationFetch: {
    type: Boolean,
    default: false,
  },
  total: {
    type: Number,
    default: 1,
  },
  page: {
    type: Number,
    default: 1,
  },
  itemsPerPage: {
    type: Number,
    default: 50,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  activeSds: {
    type: Object as PropType<Sds>,
    default: undefined,
  },
  sortState: {
    type: Object as PropType<SortState>,
    default: () => ({
      name: 'none',
      revised_at: 'none',
      created_at: 'none',
      assessed_at: 'none',
    }),
  },
  selectedSdses: {
    type: Array as PropType<Sds[]>,
    default: () => [],
  },
  isSelectedAllSdses: {
    type: Boolean,
    default: false,
  },
  isSelectedIndeterminateSdses: {
    type: Boolean,
    default: false,
  },
  isSelectedWholeSdses: {
    type: Boolean,
    default: false,
  },
  toggleSelectedSds: {
    type: Function as PropType<(sds: Sds) => void>,
    required: true,
  },
  toggleAllSelectedSdses: {
    type: Function as PropType<(check: boolean) => void>,
    required: true,
  },
  toggleWholeSelectedSdses: {
    type: Function as PropType<(check: boolean) => void>,
    required: true,
  },
  toggleSort: {
    type: Function as PropType<(column: string) => void>,
    required: true,
  },
  onClickProductName: {
    type: Function as PropType<(name: string) => void>,
    required: true,
  },
  onClickProductCode: {
    type: Function as PropType<(code: string) => void>,
    required: true,
  },
  onClickTag: {
    type: Function as PropType<(tag: TagResponse) => void>,
    required: true,
  },
  onClickStatus: {
    type: Function as PropType<(statusId: string) => void>,
    required: true,
  },
  onClickDepartment: {
    type: Function as PropType<(department: Department) => void>,
    required: true,
  },
  onClickAssigneeUsers: {
    type: Function as PropType<(userId: string) => void>,
    required: true,
  },
  isChange: {
    type: Number,
    default: 0,
  },
},
  emits: ["showSdsDetail", "update:allSdsCheck", "update:page", "update:itemsPerPage", "fetchItems", "exportCreateSimple", "exportCreateSimpleV3", "exportCreateSimpleV3_2"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'showSdsDetail', item: Sds, index: number): void;
  (e: 'update:allSdsCheck', event: boolean): void;
  (e: 'update:page', page: number): void;
  (e: 'update:itemsPerPage', itemsPerPage: number): void;
  (e: 'fetchItems'): void;
  (e: 'exportCreateSimple', sdsId: string): void;
  (e: 'exportCreateSimpleV3', sdsId: string): void;
  (e: 'exportCreateSimpleV3_2', sdsId: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);
const company = reactive({ ...readAssertedCompany(store) });

const m = defineMessages({
  nonDeletable: '削除不可',
  exportSimple: 'CREATE SIMPLEを出力',
  noPermission: 'する権限がありません',
  noTargetComponents: 'ファイルを生成できる対象成分がありません',
});





const { hasPermission, PERMISSIONS: P } = usePermissionComposable();
const { isOwnDepartment, companyDepartments: allCompanyDepartments } =
  useDepartmentComposable();
const { isFeatureActive, COMPANY_FEATURES } = useCompanyFeatureComposable();

/**
 * 親が違えば同名の部署を作れる仕様なので、名前だけでは同名の部署を区別できない
 * (3 階層以上では直近の親も同名になりうるため、1 階層上の親名だけでも不十分)。
 * 親の見える範囲は role/所属で絞られる場合があるため、絞り込み前の一覧 (フィルタ済みの
 * プロパティではなく useDepartmentComposable 由来) からフルパスを組む。
 */
const departmentPathByUuid = computed(() =>
  buildDepartmentPathMap(allCompanyDepartments.value)
);
const departmentLabel = (department: Department): string =>
  departmentPathByUuid.value.get(department.uuid) ?? department.name;
const riskAssessmentUserGuideUrl = RISK_ASSESSMENT_USER_GUIDE_URL;

const riskAssessmentModalSds = ref<Sds | null>(null);
const isRiskAssessmentModalOpen = computed({
  get: () => riskAssessmentModalSds.value !== null,
  set: (v: boolean) => {
    if (!v) riskAssessmentModalSds.value = null;
  },
});

const riskAssessmentModalProducts = computed(() =>
  (riskAssessmentModalSds.value?.sds_products ?? []).filter(
    (p) => (p.sds_risk_assessment_results ?? []).length > 0
  )
);

const openRiskAssessmentModal = (sds: Sds) => {
  riskAssessmentModalSds.value = sds;
};

const INPUT_OPERATING_CONDITION_DEFAULT = 'DEFAULT';

// 作業環境がDEFAULT(顧客未入力)のものしかないか
const isDefaultOnlyWorkingCondition = (item: Sds): boolean => {
  const workingConditions = item.sds_risk_assessment_working_conditions ?? [];
  return (
    workingConditions.length > 0 &&
    workingConditions.every(
      (wc) => wc.input_operating_condition === INPUT_OPERATING_CONDITION_DEFAULT
    )
  );
};

const isProductWorkingEnvInputRecommended = (product: SdsProduct): boolean => {
  const sds = riskAssessmentModalSds.value;
  // 作業環境がDEFAULTのみ かつ 当該製品にリスク3以上の結果がある場合に入力推奨
  if (!sds || !isDefaultOnlyWorkingCondition(sds)) return false;
  return (product.sds_risk_assessment_results ?? []).some(
    (r) => r.health_hazard >= 3 || r.physical_and_chemical_hazards >= 3
  );
};

const isProductWorkingEnvInputCompleted = (product: SdsProduct): boolean => {
  const sds = riskAssessmentModalSds.value;
  if (!sds) return false;
  if ((product.sds_risk_assessment_results ?? []).length === 0) return false;
  return (sds.sds_risk_assessment_working_conditions ?? []).some(
    (wc) => wc.input_operating_condition !== INPUT_OPERATING_CONDITION_DEFAULT
  );
};

const getProductMaxHealthHazard = (product: SdsProduct): number | null => {
  const results = product.sds_risk_assessment_results ?? [];
  if (results.length === 0) return null;
  return Math.max(...results.map((r) => r.health_hazard));
};

const getProductMaxPhysicalHazard = (product: SdsProduct): number | null => {
  const results = product.sds_risk_assessment_results ?? [];
  if (results.length === 0) return null;
  return Math.max(...results.map((r) => r.physical_and_chemical_hazards));
};

const getProductRiskAssessmentUrl = (sds: Sds, product: SdsProduct): string =>
  `${riskAssessmentBaseUrl}/${company.uuid}/risk-assessments/${sds.uuid}/${product.id}`;

const getSingleProductRiskAssessmentUrl = (item: Sds): string => {
  const product = (item.sds_products ?? []).find(
    (p) => (p.sds_risk_assessment_results ?? []).length > 0
  );
  return product ? getProductRiskAssessmentUrl(item, product) : '';
};

const updatePage = (e: number) => {
  emits('update:page', e);
};

const updateItemsPerPage = (e: number) => {
  emits('update:itemsPerPage', e);
};

const fetchItems = () => {
  emits('fetchItems');
};

const exportCreateSimple = (sdsId: string) => {
  if (!hasPermission(P.CREATE_SIMPLE_DOWNLOAD)) {
    return;
  }
  emits('exportCreateSimple', sdsId);
};

const exportCreateSimpleV3 = (sdsId: string) => {
  if (!hasPermission(P.CREATE_SIMPLE_DOWNLOAD)) {
    return;
  }
  emits('exportCreateSimpleV3', sdsId);
};

const exportCreateSimpleV3_2 = (sdsId: string) => {
  if (!hasPermission(P.CREATE_SIMPLE_DOWNLOAD)) {
    return;
  }
  emits('exportCreateSimpleV3_2', sdsId);
};

const handleUpdatePage = (event) => {
  props.toggleWholeSelectedSdses(false);
  updatePage(event);
  if (!props.suppressPaginationFetch) {
    fetchItems();
  }
};

const handleUpdateItemsPerPage = (event) => {
  updateItemsPerPage(event);
  if (!props.suppressPaginationFetch) {
    fetchItems();
  }
};

const allHeaders = [
  {
    text: '',
    value: 'check',
    sortable: false,
    width: '30px',
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.sdsName),
    value: 'name',
    sortable: false,
    width: '200px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.productName),
    value: 'uuid',
    sortable: false,
    width: '100px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.manufacturers),
    value: 'manufacturers',
    sortable: false,
    width: '160px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.analysis_status),
    value: 'status_id',
    sortable: false,
    width: '160px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.pdfUploadDate),
    value: 'created_at',
    sortable: false,
    width: '160px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.sdsRevisedAt),
    value: 'revised_at',
    sortable: false,
    width: '140px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.supplierConfirmedAt),
    value: 'supplier_confirmed_at',
    sortable: false,
    width: '190px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.revisionStatus),
    sortable: false,
    value: 'revision_status',
    width: '120px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.assignee),
    value: 'assignee_users',
    sortable: false,
    width: '155px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.department),
    value: 'departments',
    sortable: false,
    width: '120px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.tag),
    value: 'tags',
    sortable: false,
    width: '60px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.productCode),
    value: 'product_code_list',
    sortable: false,
    width: '120px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.riskAssessmentWorkingEnvRecommendation),
    value: 'working_env_recommendation',
    sortable: false,
    width: '140px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.riskAssessment),
    value: 'risk_assessment',
    sortable: false,
    width: '160px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.riskAssessmentDate),
    value: 'assessed_at',
    sortable: false,
    width: '220px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(m.exportSimple),
    value: 'actions',
    sortable: false,
    width: '160px',
    class: 'grey lighten-3 font-weight-bold',
  },
];

const headers = computed(() => {
  const isRiskAssessmentActive = isFeatureActive(
    COMPANY_FEATURES.CHEMICAN_RISK_ASSESSMENT
  );
  return allHeaders.filter(
    (h) =>
      isRiskAssessmentActive ||
      !['working_env_recommendation', 'risk_assessment'].includes(h.value)
  );
});

const statusIdList = [StatusId.SECOND_CHECK_DONE.value];

const footerProps = {
  'items-per-page-options': [50, 100, 200],
  'items-per-page-text': $t(sharedMessages.itemsPerPageText),
};

const disabledExportCreateSimple = (item: Sds): boolean => {
  let count = 0;
  for (const product of item.sds_products) {
    for (const material of product.sds_section3.material_list) {
      for (const cas of material.cas_list) {
        if (cas.cas_number.match(casNumberPattern)) {
          count++;
        }
      }
    }
  }
  return count > 0;
};

const pageSelectItems = computed((): Array<FormItem> => {
  return [...Array(Math.ceil(props.total / props.itemsPerPage)).keys()].map(
    (i) => {
      return {
        value: i + 1,
        text: `${i + 1} / ${Math.ceil(props.total / props.itemsPerPage)} (${$t(
          sharedMessages.totalItems,
          { total: props.total.toLocaleString() }
        )})`,
      };
    }
  );
});

const isSelectedSds = (targetUuid: Sds['uuid']) =>
  props.selectedSdses.some((sds) => sds.uuid === targetUuid);

const isActiveSds = (uuid: string): boolean => {
  return uuid === props.activeSds?.uuid;
};

const isWorkingEnvInputRecommended = (item: Sds): boolean => {
  // 作業環境がDEFAULT(顧客未入力)のみ かつ リスク3以上の結果がある場合に入力推奨
  if (!isDefaultOnlyWorkingCondition(item)) return false;
  return getAllResults(item).some(
    (r) => r.health_hazard >= 3 || r.physical_and_chemical_hazards >= 3
  );
};

// リスク評価結果が存在し、DEFAULT以外(顧客入力済み)の作業環境が1つでもあれば入力済み
const isWorkingEnvInputCompleted = (item: Sds): boolean => {
  if (getAllResults(item).length === 0) return false;
  return (item.sds_risk_assessment_working_conditions ?? []).some(
    (wc) => wc.input_operating_condition !== INPUT_OPERATING_CONDITION_DEFAULT
  );
};

const getAllResults = (item: Sds) => {
  return (item.sds_products ?? []).flatMap(
    (p) => p.sds_risk_assessment_results ?? []
  );
};

const getMaxHealthHazardLevel = (item: Sds): number | null => {
  const results = getAllResults(item);
  if (results.length === 0) return null;
  return Math.max(...results.map((r) => r.health_hazard));
};

const getMaxPhysicalHazardLevel = (item: Sds): number | null => {
  const results = getAllResults(item);
  if (results.length === 0) return null;
  return Math.max(...results.map((r) => r.physical_and_chemical_hazards));
};

const getProductsWithResultsCount = (item: Sds): number => {
  return (item.sds_products ?? []).filter(
    (p) => (p.sds_risk_assessment_results ?? []).length > 0
  ).length;
};

onMounted(async () => {
  nextTick(() => {
    document.onscroll = () => {
      const TOP_THRESHOLD = 77;
      const tableElement = document.querySelector(
        '.v-data-table__wrapper > table'
      );
      const headerElement = document.querySelector<HTMLElement>(
        '.v-data-table-header'
      );
      if (!tableElement || !headerElement) {
        return;
      }

      const tableTopPx = tableElement.getBoundingClientRect().top;

      if (tableTopPx < TOP_THRESHOLD) {
        headerElement.style.top = TOP_THRESHOLD - tableTopPx + 'px';
      } else {
        headerElement.style.top = 0 + 'px';
      }
    };
  });
});

onUnmounted(() => {
  document.onscroll = null;
});

return { __sfc: true,$t, company, m, props, emits, hasPermission, P, isOwnDepartment, allCompanyDepartments, isFeatureActive, COMPANY_FEATURES, departmentPathByUuid, departmentLabel, riskAssessmentUserGuideUrl, riskAssessmentModalSds, isRiskAssessmentModalOpen, riskAssessmentModalProducts, openRiskAssessmentModal, INPUT_OPERATING_CONDITION_DEFAULT, isDefaultOnlyWorkingCondition, isProductWorkingEnvInputRecommended, isProductWorkingEnvInputCompleted, getProductMaxHealthHazard, getProductMaxPhysicalHazard, getProductRiskAssessmentUrl, getSingleProductRiskAssessmentUrl, updatePage, updateItemsPerPage, fetchItems, exportCreateSimple, exportCreateSimpleV3, exportCreateSimpleV3_2, handleUpdatePage, handleUpdateItemsPerPage, allHeaders, headers, statusIdList, footerProps, disabledExportCreateSimple, pageSelectItems, isSelectedSds, isActiveSds, isWorkingEnvInputRecommended, isWorkingEnvInputCompleted, getAllResults, getMaxHealthHazardLevel, getMaxPhysicalHazardLevel, getProductsWithResultsCount, getJoinedDepartmentName, StatusId, RevisionStatus, CountryCode, omitString, formatToDate, SdsTablePseudoScrollBar, TagChip, sharedMessages, getRiskLevelChipColor }
}

})